import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { Box, ButtonAlpha, Grid, Stack, useBreakpoints, useIsClient } from '@yonderland/andes-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import { makeGetProductDefaults, makeGetSelectedColorId, makeGetSelectedSku, } from '../../../redux/product/selectors/ProductSelector';
import { ScrollUtil } from '../../../util/ScrollUtil';
import { ProductMediaLightBox } from './light-box/ProductMediaLightBox';
import { ProductMediaLightBoxContext } from './light-box/ProductMediaLightBoxContext';
import { ProductMediaCollageItem } from './ProductMediaCollageItem';
import { useProductMedia } from './useProductMedia';
const SHOWN_COLLAGE_ITEMS = 4;
const COLLAGE_TEST_ID = 'product_collage';
export const ProductMediaCollage = () => {
    const { t } = useTranslation(I18N_NAMESPACES.product);
    const { isShopLanding } = useAemConfig();
    const { mediaCollection, alt } = useProductMedia();
    const selectedColorId = useSelector(makeGetSelectedColorId());
    const selectedSku = useSelector(makeGetSelectedSku());
    const { productCode, productId } = useSelector(makeGetProductDefaults());
    const { isSm, isMd } = useBreakpoints();
    const [showAllMedia, setShowAllMedia] = useState(false);
    const [startSlideIndex, setStartSlideIndex] = useState(0);
    const isClient = useIsClient();
    const wishListProduct = {
        colorId: selectedColorId,
        productCode: productCode,
        productId: parseInt(productId, 10),
        sku: selectedSku,
    };
    const collectionContainsVideos = useMemo(() => mediaCollection.some(m => m.isVideo), [mediaCollection]);
    const getMediaCollectionIndex = (item) => {
        return mediaCollection.findIndex(m => m === item);
    };
    const getVideoButtonPosition = (totalShown) => {
        // Display on first image if only one image, otherwise on second image
        return totalShown === 1 ? 0 : 1;
    };
    const shouldRenderVideoButton = (index, totalShown) => {
        if (!collectionContainsVideos) {
            return false;
        }
        return index === getVideoButtonPosition(totalShown);
    };
    const handleClickImage = (_e, index) => {
        setStartSlideIndex(index);
    };
    const handlePlayVideo = () => {
        const firstVideoIndex = mediaCollection.findIndex(m => m.isVideo);
        if (firstVideoIndex !== -1) {
            setStartSlideIndex(firstVideoIndex);
        }
    };
    const productCollageItemProps = {
        wishListProduct,
        onPlayVideoClick: handlePlayVideo,
        onClick: handleClickImage,
    };
    const [shownMediaCollection, hiddenMediaCollection] = useMemo(() => {
        const nonVideoMedia = mediaCollection.filter(m => !m.isVideo);
        return [nonVideoMedia.slice(0, SHOWN_COLLAGE_ITEMS), nonVideoMedia.slice(SHOWN_COLLAGE_ITEMS)];
    }, [mediaCollection]);
    const handleClickShowAll = () => {
        setShowAllMedia(prev => !prev);
    };
    return shownMediaCollection.length > 0 ? (_jsx(ProductMediaLightBoxContext.Provider, { value: { startIndex: startSlideIndex, mediaCollection, alt }, children: _jsx(ProductMediaLightBox, { children: _jsx(Box, { paddingBlockEnd: "4", position: { md: 'sticky' }, top: { md: '2' }, children: shownMediaCollection.length === 1 ? (_jsx(Grid, { testId: COLLAGE_TEST_ID, columns: {
                        xs: 2,
                        lg: 4,
                    }, gap: 2, children: _jsx(Grid.Cell, { span: 2, offset: { lg: 2 }, children: _jsx(ProductMediaCollageItem, Object.assign({ index: 0, item: shownMediaCollection[0], showLabels: true, showVideoButton: collectionContainsVideos, showWishlistButton: !isShopLanding }, productCollageItemProps)) }) })) : (_jsxs(Grid, { testId: COLLAGE_TEST_ID, columns: {
                        xs: 1,
                        lg: 2,
                    }, gap: 2, children: [shownMediaCollection.map((item, index) => (_jsx(ProductMediaCollageItem, Object.assign({ index: getMediaCollectionIndex(item), item: item, showLabels: index === 0, showVideoButton: shouldRenderVideoButton(index, shownMediaCollection.length), showWishlistButton: !isShopLanding && isClient && index === (isSm || isMd ? 0 : 1) }, productCollageItemProps), index))), hiddenMediaCollection.length > 0 &&
                            (!showAllMedia ? (_jsx(Grid.Cell, { span: { lg: 2 }, children: _jsx(Box, { paddingBlockStart: "3", children: _jsx(Stack, { justify: "center", children: _jsx(ButtonAlpha, { testId: "product_collage_show_more", theme: "branded", variant: "secondary", size: "md", text: t('pdp.collage.show.more'), onClick: handleClickShowAll }) }) }) })) : (_jsxs(_Fragment, { children: [hiddenMediaCollection.map((item, index) => (_jsx(ProductMediaCollageItem, Object.assign({ index: getMediaCollectionIndex(item), item: item }, productCollageItemProps), index))), showAllMedia && (_jsx(Grid.Cell, { span: { lg: 2 }, children: _jsx(Box, { paddingBlockStart: "3", children: _jsx(Stack, { justify: "center", children: _jsx(ButtonAlpha, { testId: "product_collage_show_less", theme: "branded", variant: "secondary", size: "md", text: t('pdp.collage.show.less'), onClick: () => {
                                                        handleClickShowAll();
                                                        ScrollUtil.scrollToTop();
                                                    } }) }) }) }))] })))] })) }) }) })) : null;
};
